<template>
  <div class="routing-status-card">
    <div class="routing-status-card__row">
      <span class="row-left">Prefix:</span>
      <span v-if="!aggregations" class="row-right">{{ delegatedPrefix }}</span>
      <div v-else class="row-right aggregation">
        <div
          v-if="isSmallScreen && !isLargeScreen"
          class="aggregations-toggle small-screen"
          @click="isExpandAggregationList = !isExpandAggregationList"
        >
          <span class="prefix mr-1">
            {{ delegatedPrefix }}
          </span>
          <div class="d-flex align-items-center">
            <span class="badge badge-primary mr-2">
              Aggregation
            </span>
            <div class="icon-wrapper">
              <fa
                icon="chevron-down"
                :class="{ 'fa-rotate-180': isExpandAggregationList }"
              ></fa>
            </div>
          </div>
        </div>
        <div
          v-else
          class="aggregations-toggle"
          @click="isExpandAggregationList = !isExpandAggregationList"
        >
          <div class="prefix-wrapper">
            <span class="prefix">
              {{ delegatedPrefix }}
            </span>
            <span class="badge badge-primary">
              Aggregation
            </span>
          </div>
          <div class="icon-wrapper">
            <fa
              icon="chevron-down"
              :class="{ 'fa-rotate-180': isExpandAggregationList }"
            ></fa>
          </div>
        </div>
        <div v-if="isExpandAggregationList" class="aggregations-list">
          <ul>
            <li v-for="(item, index) in aggregations" :key="index">
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="routing-status-card__row">
      <span class="row-left">BGP route:</span>
      <span class="row-right">{{ routePrefix }}</span>
    </div>
    <div class="routing-status-card__row">
      <span class="row-left">Origin AS:</span>
      <span class="row-right">AS{{ originAS }}</span>
    </div>
    <div class="routing-status-card__row">
      <span class="row-left">ROA:</span>
      <span
        v-if="ROAStatus === 'Published'"
        class="row-right prefix-icon green-dot"
        >Published</span
      >
      <span
        v-else-if="ROAStatus === 'Not published'"
        class="row-right prefix-icon yellow-dot"
        >Not published</span
      >
      <span
        v-else-if="ROAStatus === 'Mismatch'"
        class="row-right prefix-icon red-dot"
        @click="showROAMismatchModal"
      >
        <span class="mismatch">Mismatch</span>
        <span class="mismatch info">+ info</span>
      </span>
    </div>
    <div class="routing-status-card__row">
      <span class="row-left">Route object:</span>
      <span
        v-if="routeObjectStatus === 'Published'"
        class="row-right prefix-icon green-dot"
        >Published</span
      >
      <span
        v-else-if="routeObjectStatus === 'Not published'"
        class="row-right prefix-icon yellow-dot"
        >Not published</span
      >
      <span
        v-else-if="routeObjectStatus === 'Mismatch'"
        class="row-right prefix-icon red-dot"
        @click="showRouteObjectMismatchModal"
      >
        <span class="mismatch">Mismatch</span>
        <span class="mismatch info">+ info</span>
      </span>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import {
  ROA_STATUS,
  ROUTE_OBJECT_STATUS,
  MISMATCH_TYPE
} from '../../constants/routingStatusConstant';

export default {
  name: 'RoutingStatusCard',
  props: {
    routingStatusData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isExpandAggregationList: false
    };
  },
  computed: {
    ...mapState(['isSmallScreen', 'isLargeScreen']),
    delegatedPrefix() {
      return this.routingStatusData.isPrefixHolder
        ? this.routingStatusData.delegatedPrefix
        : `*${this.routingStatusData.delegatedPrefix}`;
    },
    aggregations() {
      return this.routingStatusData.aggregations;
    },
    routePrefix() {
      return this.routingStatusData.route.prefix;
    },
    originAS() {
      return this.routingStatusData.route.asn;
    },
    ROAStatus() {
      let status = '';

      switch (this.routingStatusData.roa_status) {
        case ROA_STATUS.PUBLISHED:
          status = 'Published';
          break;
        case ROA_STATUS.NOT_PUBLISHED:
          status = 'Not published';
          break;
        case ROA_STATUS.MISMATCH:
          status = 'Mismatch';
          break;
        default:
          break;
      }

      return status;
    },
    routeObjectStatus() {
      let status = '';

      switch (this.routingStatusData.route_object_status) {
        case ROUTE_OBJECT_STATUS.PUBLISHED:
          status = 'Published';
          break;
        case ROUTE_OBJECT_STATUS.NOT_PUBLISHED:
          status = 'Not published';
          break;
        case ROUTE_OBJECT_STATUS.MISMATCH:
          status = 'Mismatch';
          break;
        default:
          break;
      }

      return status;
    }
  },
  methods: {
    ...mapMutations('routingStatusStore', [
      'set_isShowMismatchModal',
      'set_mismatchModalData'
    ]),
    showROAMismatchModal() {
      this.set_isShowMismatchModal(true);
      this.set_mismatchModalData({
        type: MISMATCH_TYPE.ROA,
        data: this.routingStatusData
      });
    },
    showRouteObjectMismatchModal() {
      this.set_isShowMismatchModal(true);
      this.set_mismatchModalData({
        type: MISMATCH_TYPE.ROUTE_OBJECT,
        data: this.routingStatusData
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.routing-status-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0.5rem 0;
  padding-left: 1rem;
  padding-top: 1rem;
  background-color: $background-white;

  &__row {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    min-height: 3rem;
  }

  .row-left {
    width: 7.5rem;
    font-weight: 600;
    display: flex;

    @media screen and (max-width: 992px) {
      flex: 0 1 40%;
    }

    @media screen and (max-width: 576px) {
      flex: 0 1 8rem;
    }
  }

  .row-right {
    .mismatch {
      display: block;
      cursor: pointer;
    }
    .info {
      display: block;
      position: relative;
      top: -0.2rem;
      width: 6rem;
      color: $text-light-blue;
    }
  }

  .aggregations-toggle {
    display: flex;
    flex-direction: row;
    align-items: center;

    cursor: pointer;

    .prefix-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .badge {
      border-radius: 0.25rem;
      font-weight: 600;
    }

    .icon-wrapper {
      padding-top: 0.5rem;
      padding-right: 0.5rem;
      padding-left: 1rem;

      svg {
        color: rgba(0, 0, 0, 0.5);
        font-size: 1.2rem;
        transition: all 0.3s;
      }
    }
  }

  .aggregations-toggle.small-screen {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;

    .icon-wrapper {
      padding-left: 0;
    }
  }

  .aggregations-list {
    ul {
      list-style: none;
      margin-top: 0.2rem;
      margin-left: 0.5rem;
      margin-bottom: 0.5rem;

      li {
        // margin: 0;
        padding: 0.2rem 0;
        font-style: italic;
        font-size: 0.8rem;
      }
    }
  }
}
</style>
